import React, { KeyboardEvent, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { FormProvider, useForm } from 'react-hook-form';
import { useDebounce } from 'usehooks-ts';
import { useAtom } from 'jotai';
import { documentFilterState } from '@/recoil/atom';
import { useRecordFilterPassportSearch } from '@/hooks/apiHooks';
import Input from '@/components/Approval/LeftContainer/Input';
import PassportCheckItem from '@/components/Approval/Common/PassportCheckItem';
import AllFilterOption from '@/components/Approval/LeftContainer/PeopleFilter/AllFilterOption';

type PeopleField = 'createdBy' | 'internalParticipants';

type PassportDetail = {
  name: string;
  positionName?: string;
  divisionName?: string;
  departmentName?: string;
  profileImageUrl?: string;
};

type Props = {
  field: PeopleField;
  placeholder?: string;
  /** 추천(최근) 목록 — 있으면 검색 전 '추천' 섹션으로 노출 */
  recommend?: { passportId: string; detail: PassportDetail }[];
  /** AND(모두 포함)/OR(한 명이라도 포함) 옵션 — 있으면 패널 상단에 노출 */
  allFilter?: { checked: boolean; onChange: (all: boolean) => void };
};

/**
 * 회의록 사람 검색(작성자 / 내부 참석자) 패널 — 전자결재 PeopleFilterPanel 과 동일 디자인.
 * full-bleed 검색 + 인라인 검색결과 + 선택 목록. 값은 documentFilterState[field] 를 갱신.
 */
const DocumentPeoplePanel = ({
  field,
  placeholder,
  recommend,
  allFilter,
}: Props) => {
  const [state, setState] = useAtom(documentFilterState);
  const methods = useForm({ defaultValues: { keyword: '' } });
  const searchString = methods.watch('keyword');
  const debouncedValue = useDebounce(searchString, 500);

  const { data } = useRecordFilterPassportSearch(
    { keyword: debouncedValue, listSize: 50 },
    2,
  );
  const searchingData = data?.data;

  const [cursor, setCursor] = useState(-1);
  const [isMovingMouse, setIsMovingMouse] = useState(false);
  const navStartedRef = useRef(false);
  const lastKeyTsRef = useRef(0);
  const scrollLayout = useRef<HTMLDivElement>(null);

  const selected = state[field] ?? [];

  const isChecked = (passportId: string) =>
    selected.some((p) => p.passportId === passportId);

  const toggle = (
    person: { passportId: string; name: string } & Partial<PassportDetail>,
  ) => {
    const { passportId } = person;
    if (isChecked(passportId)) {
      setState({
        ...state,
        [field]: selected.filter((p) => p.passportId !== passportId),
      });
    } else {
      if (selected.length >= 10) return;
      setState({ ...state, [field]: [...selected, person] });
    }
  };

  useEffect(() => {
    setCursor(-1);
    navStartedRef.current = false;
  }, [debouncedValue]);

  useEffect(() => {
    if (!isMovingMouse && cursor >= 0) {
      // 첫 자식은 '검색결과 N건' 헤더라 실제 항목은 cursor + 1 위치
      const el = scrollLayout.current?.children[cursor + 1] as HTMLElement;
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isMovingMouse, cursor]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!searchingData) return;
    if (event.timeStamp === lastKeyTsRef.current) return;
    lastKeyTsRef.current = event.timeStamp;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setIsMovingMouse(false);
        if (!navStartedRef.current) {
          navStartedRef.current = true;
          setCursor(0);
        } else {
          setCursor((prev) => Math.min(prev + 1, searchingData.length - 1));
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        setIsMovingMouse(false);
        navStartedRef.current = true;
        setCursor((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter': {
        const item = searchingData[cursor];
        if (item) toggle({ passportId: item.passportId, ...item.detail });
        break;
      }
      case 'Escape':
        setCursor(-1);
        methods.resetField('keyword');
        break;
    }
  };

  const handleMouseMove = (index: number) => {
    setIsMovingMouse(true);
    navStartedRef.current = false;
    setCursor(index);
  };

  return (
    <FormProvider {...methods}>
      {allFilter && (
        <AllFilterOption
          checked={allFilter.checked}
          onChange={allFilter.onChange}
        />
      )}
      <Input
        value={'keyword'}
        placeholder={placeholder}
        handleKeyDown={handleKeyDown}
        onBlur={() => setCursor(-1)}
        bleed
      />
      {debouncedValue ? (
        <ResultLayout ref={scrollLayout}>
          {data ? (
            <>
              <ResultCount>
                검색결과 <b>{searchingData?.length ?? 0}</b>건
              </ResultCount>
              {searchingData && searchingData.length > 0 ? (
                searchingData.map((res, idx) => (
                  <RowWrap
                    key={res.passportId}
                    target={idx === cursor}
                    onMouseMove={() => handleMouseMove(idx)}
                  >
                    <PassportCheckItem
                      passportId={res.passportId}
                      name={res.detail.name}
                      positionName={res.detail.positionName}
                      divisionName={res.detail.divisionName}
                      departmentName={res.detail.departmentName}
                      profileImageUrl={res.detail.profileImageUrl}
                      isChecked={isChecked(res.passportId)}
                      onClick={() =>
                        toggle({ passportId: res.passportId, ...res.detail })
                      }
                      keyword={debouncedValue}
                    />
                  </RowWrap>
                ))
              ) : (
                <Empty>
                  일치하는 결과가 없어요.
                  <br />
                  검색어를 다시 한번 확인해 주세요.
                </Empty>
              )}
            </>
          ) : (
            <Empty>검색 중…</Empty>
          )}
        </ResultLayout>
      ) : (
        (() => {
          const recommendItems = (recommend ?? []).filter(
            (r) => !isChecked(r.passportId),
          );
          if (selected.length === 0 && recommendItems.length === 0)
            return null;
          return (
            <SelectedLayout>
              {selected.map((p) => (
                <RowWrap key={p.passportId}>
                  <PassportCheckItem
                    passportId={p.passportId}
                    name={p.name}
                    positionName={p.positionName}
                    divisionName={p.divisionName}
                    departmentName={p.departmentName}
                    profileImageUrl={p.profileImageUrl}
                    isChecked
                    onClick={() => toggle(p)}
                  />
                </RowWrap>
              ))}
              {recommendItems.length > 0 && (
                <>
                  {selected.length > 0 && <RecoDivider />}
                  <RecoLabel>추천</RecoLabel>
                  {recommendItems.map((r) => (
                    <RowWrap key={`reco-${r.passportId}`}>
                      <PassportCheckItem
                        passportId={r.passportId}
                        name={r.detail.name}
                        positionName={r.detail.positionName}
                        divisionName={r.detail.divisionName}
                        departmentName={r.detail.departmentName}
                        profileImageUrl={r.detail.profileImageUrl}
                        isChecked={isChecked(r.passportId)}
                        onClick={() =>
                          toggle({ passportId: r.passportId, ...r.detail })
                        }
                      />
                    </RowWrap>
                  ))}
                </>
              )}
            </SelectedLayout>
          );
        })()
      )}
    </FormProvider>
  );
};

export default DocumentPeoplePanel;

/** 전자결재 사람 행과 동일 — padding 4px + hover/키보드 타깃 배경 */
const RowWrap = styled.div<{ target?: boolean }>`
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  * {
    cursor: pointer;
  }
  background: ${({ target }) => (target ? 'var(--dk-bg-subtle, #f9fafb)' : 'transparent')};
  &:hover {
    background: var(--dk-bg-subtle, #f9fafb);
  }
`;

const ResultLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 240px;
  overflow-y: auto;
  padding: 8px;
`;

const SelectedLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
`;

const ResultCount = styled.div`
  font-size: 12px;
  line-height: 18px;
  color: var(--dk-text, black);
  b {
    color: var(--dk-accent, #0066cc);
    font-weight: 600;
  }
`;

const Empty = styled.div`
  color: var(--dk-text-dim, #b7bbc2);
  font-size: 14px;
  line-height: 20px;
  padding: 4px;
`;

const RecoLabel = styled.div`
  color: var(--dk-text-secondary, #888d96);
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
`;

/** 선택 항목 ↔ 추천 사이 full-bleed 구분선 */
const RecoDivider = styled.div`
  height: 1px;
  background: var(--dk-bg-strong, #e7e9ec);
  margin: 0 -8px;
`;
